/**
 * Chat API
 * API calls for testing agent chat functionality
 */

import userApiClient from './userClient';
import { API_BASE_URL } from './client';

const INCOMPLETE_STREAM_ERROR = 'Stream ended before completion';
const EMPTY_STREAM_ERROR = 'Agent completed without a response';
const INVALID_SOULMATE_RESPONSE_ERROR = 'Agent returned an invalid Soulmate response';

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export interface ImageInput {
  url: string;
  detail?: 'auto' | 'low' | 'high';
}

export interface ChatRequest {
  messages: ChatMessage[];
  stream?: boolean;
  topic_id?: string;
  temperature?: number;
  max_tokens?: number;
  images?: ImageInput[]; // Multimodal image input
}

export interface ChatTopic {
  topic_id: string;
  title: string;
  topic_name?: string;
  message_count: number;
  created_at: string;
  last_active?: string;
  updated_at?: string;
}

export interface TopicMessage {
  id: string;
  role: string;
  content: string;
  created_at: string;
  tool_calls?: any[];
  metrics?: any;
}

export interface ToolArtifact {
  artifact_type: string;
  url: string;
  object_name: string;
}

export interface ToolCallInfo {
  name: string;
  id: string;
  status: 'running' | 'completed' | 'error';
  startTime: number;
  executionTime?: number;
  progress?: string;
  images?: Array<{ url: string; object_name?: string; mime_type?: string }>;
  charts?: ToolArtifact[];  // Chart SVG artifacts from history
  html_contents?: ToolArtifact[];  // HTML content artifacts from history
  input?: Record<string, unknown>; // Tool input arguments (for reasoning tools)
  isReasoning?: boolean; // Flag indicating this is a reasoning tool
}

export interface StreamMetrics {
  input_tokens?: number;
  output_tokens?: number;
  total_tokens?: number;
}

export interface ChartSvgEvent {
  event_type: 'chart_svg';
  chart_type: 'natal' | 'transit' | 'synastry' | 'composite' | 'return';
  svg_content: string;
  svg_url?: string;  // Presigned URL for loading SVG from history
  subject_name: string;
  chart_title?: string;
  theme: string;
  output_type: string;
  width: number;
  height: number;
  metadata?: Record<string, any>;
}

export interface HtmlContentEvent {
  event_type: 'html_content';
  content_type: string;
  html_content: string;
  html_url?: string;  // Presigned URL for loading HTML from history
  title: string;
  description?: string;
  width: number;
  height: number;
  expandable?: boolean;
  allow_scripts?: boolean;
  artifact_id?: string;
  metadata?: Record<string, any>;
}

export interface StreamCallbacks {
  onInit?: (event: any) => void;
  onContent?: (content: string) => void;
  onToolCallStart?: (tool: { name: string; id: string; input?: Record<string, unknown> }) => void;
  onToolInputUpdate?: (toolId: string, input: Record<string, unknown>) => void;  // For streaming tool input (reasoning tools)
  onToolCallEnd?: (metadata: { toolName?: string; executionTime?: number; images?: Array<{ url: string; object_name?: string; mime_type?: string }> }) => void;
  onToolProgress?: (toolId: string, progress: string) => void;
  onChartSvg?: (chart: ChartSvgEvent) => void;  // For chart visualization events
  onHtmlContent?: (content: HtmlContentEvent) => void;  // For HTML content events (K-line, reports)
  onMetrics?: (metrics: StreamMetrics) => void;
  onDone?: (event: any) => void;
  onError?: (error: string) => void;
}

export interface VoiceStreamCallbacks extends StreamCallbacks {
  onTranscript?: (text: string) => void;
}

function createStreamLifecycle(callbacks: StreamCallbacks) {
  let terminalCallbackFired = false;
  let responseObserved = false;

  return {
    isTerminal: () => terminalCallbackFired,
    markResponse: () => {
      if (terminalCallbackFired) {
        return;
      }
      responseObserved = true;
    },
    complete: (event: any) => {
      if (terminalCallbackFired) {
        return;
      }
      terminalCallbackFired = true;
      if (responseObserved) {
        callbacks.onDone?.(event);
      } else {
        callbacks.onError?.(EMPTY_STREAM_ERROR);
      }
    },
    fail: (error: string) => {
      if (terminalCallbackFired) {
        return;
      }
      terminalCallbackFired = true;
      callbacks.onError?.(error);
    },
  };
}

function soulmateSpeechText(event: any): string | null {
  const hasOnlyKeys = (value: any, keys: string[]) => (
    value !== null
    && typeof value === 'object'
    && !Array.isArray(value)
    && Object.keys(value).every((key) => keys.includes(key))
  );

  if (
    !hasOnlyKeys(event, ['type', 'protocol_version', 'group_id', 'parts'])
    || event.type !== 'soulmate_response'
    || event.protocol_version !== 'soulmate.response.v2'
    || typeof event.group_id !== 'string'
    || event.group_id.length === 0
    || !Array.isArray(event.parts)
    || event.parts.length === 0
  ) {
    return null;
  }

  const segmentKinds = new Set(['state', 'action', 'scene', 'speech']);
  const speechParts = event.parts.map((part: any, partIndex: number) => {
    if (
      !hasOnlyKeys(part, ['part_id', 'index', 'segments'])
      || part.part_id !== `${event.group_id}:${partIndex}`
      || part.index !== partIndex
      || !Array.isArray(part.segments)
      || part.segments.length === 0
    ) {
      return null;
    }

    const normalizedSegments = part.segments.map((segment: any) => {
      if (
        !hasOnlyKeys(segment, ['kind', 'text'])
        || !segmentKinds.has(segment.kind)
        || typeof segment.text !== 'string'
        || segment.text.trim().length === 0
      ) {
        return null;
      }
      return { kind: segment.kind, text: segment.text.trim() };
    });
    if (normalizedSegments.some((segment: any) => segment === null)) {
      return null;
    }

    const speechSegments = normalizedSegments.filter((segment: any) => segment.kind === 'speech');
    const presentationKinds = normalizedSegments
      .slice(0, -1)
      .map((segment: any) => segment.kind);
    if (
      speechSegments.length !== 1
      || normalizedSegments[normalizedSegments.length - 1].kind !== 'speech'
      || presentationKinds.length !== new Set(presentationKinds).size
    ) {
      return null;
    }
    return speechSegments[0].text;
  });
  if (speechParts.some((part: string | null) => part === null)) {
    return null;
  }
  return (speechParts as string[]).join('\n');
}

export const chatApi = {
  /**
   * Send chat message (non-streaming)
   */
  send: async (agentType: string, request: ChatRequest): Promise<any> => {
    const { data } = await userApiClient.post(`/agents/${agentType}/chat`, {
      ...request,
      stream: false,
    });
    return data;
  },

  /**
   * Transcribe audio to text only (no chat)
   */
  transcribeAudio: async (audioBlob: Blob): Promise<string> => {
    const token = localStorage.getItem('admin_token');
    const userId = localStorage.getItem('admin_user_id');

    const formData = new FormData();
    // Determine file extension based on MIME type
    const ext = audioBlob.type.includes('webm') ? 'webm' :
                audioBlob.type.includes('ogg') ? 'ogg' :
                audioBlob.type.includes('mp4') ? 'm4a' : 'wav';
    formData.append('file', audioBlob, `recording.${ext}`);
    formData.append('model', 'glm-asr-2512');

    const response = await fetch(`${API_BASE_URL}/v1/audio/transcriptions`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-User-ID': userId || '',
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.error || 'Transcription failed');
    }

    const data = await response.json();
    return data.text;
  },

  /**
   * Send voice message with streaming response
   * Uses /v1/chat/completions/audio endpoint
   */
  sendVoiceStreaming: (
    agentType: string,
    audioBlob: Blob,
    topicId: string | null,
    callbacks: VoiceStreamCallbacks
  ): AbortController => {
    const controller = new AbortController();
    const lifecycle = createStreamLifecycle(callbacks);

    const token = localStorage.getItem('admin_token');
    const userId = localStorage.getItem('admin_user_id');

    const formData = new FormData();
    // Determine file extension based on MIME type
    const ext = audioBlob.type.includes('webm') ? 'webm' :
                audioBlob.type.includes('ogg') ? 'ogg' :
                audioBlob.type.includes('mp4') ? 'm4a' : 'wav';
    formData.append('audio_file', audioBlob, `recording.${ext}`);
    formData.append('model', agentType);
    formData.append('stream', 'true');
    if (topicId) {
      formData.append('topic_id', topicId);
    }

    fetch(`${API_BASE_URL}/v1/chat/completions/audio?stream_format=anthropic`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-User-ID': userId || '',
        'X-Stream-Format': 'anthropic',
        'X-Client-Source': 'admin-console',
      },
      body: formData,
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! status: ${response.status}, ${errorText}`);
        }

        // Extract transcribed text from header
        const transcribedText = response.headers.get('X-Transcribed-Text');
        if (transcribedText) {
          callbacks.onTranscript?.(decodeURIComponent(transcribedText));
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No reader available');
        }

        const decoder = new TextDecoder();
        let buffer = '';
        let streamDone = false;

        while (!streamDone) {
          const { done, value } = await reader.read();

          if (done) {
            streamDone = true;
            lifecycle.fail(INCOMPLETE_STREAM_ERROR);
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              continue;
            }

            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();

              if (data === '[DONE]') {
                lifecycle.complete({});
                streamDone = true;
                break;
              }

              try {
                const event = JSON.parse(data);

                // Handle Anthropic format events (same as text streaming)
                if (event.type === 'message_start') {
                  callbacks.onInit?.(event);
                } else if (event.type === 'content_block_start') {
                  if (event.content_block?.type === 'tool_use') {
                    lifecycle.markResponse();
                    callbacks.onToolCallStart?.({
                      name: event.content_block.name,
                      id: event.content_block.id,
                      input: event.content_block.input,
                    });
                  }
                } else if (event.type === 'content_block_delta') {
                  if (event.delta?.type === 'text_delta' && event.delta.text) {
                    lifecycle.markResponse();
                    callbacks.onContent?.(event.delta.text);
                  }
                } else if (event.type === 'content_block_stop') {
                  if (event.x_agno_tool_name && callbacks.onToolCallEnd) {
                    callbacks.onToolCallEnd({
                      toolName: event.x_agno_tool_name,
                      executionTime: event.x_agno_execution_time,
                      images: event.x_agno_tool_images || [],
                    });
                  }
                } else if (event.type === 'tool_metadata') {
                  callbacks.onToolCallEnd?.({
                    toolName: event.toolName,
                    executionTime: event.executionTime,
                    images: event.images || [],
                  });
                } else if (event.type === 'tool_progress') {
                  if (event.tool_id && event.message) {
                    callbacks.onToolProgress?.(event.tool_id, event.message);
                  }
                } else if (event.type === 'chart_svg' || event.event_type === 'chart_svg') {
                  lifecycle.markResponse();
                  // Chart visualization event from astrology tools
                  callbacks.onChartSvg?.({
                    event_type: 'chart_svg',
                    chart_type: event.chart_type || 'natal',
                    svg_content: event.svg_content || '',
                    subject_name: event.subject_name || '',
                    chart_title: event.chart_title,
                    theme: event.theme || 'classic',
                    output_type: event.output_type || 'full',
                    width: event.width || 870,
                    height: event.height || 550,
                    metadata: event.metadata,
                  });
                } else if (event.type === 'html_content' || event.event_type === 'html_content') {
                  lifecycle.markResponse();
                  callbacks.onHtmlContent?.({
                    event_type: 'html_content',
                    content_type: event.content_type || 'custom',
                    html_content: event.html_content || '',
                    title: event.title || '',
                    description: event.description,
                    width: event.width || 800,
                    height: event.height || 500,
                    expandable: event.expandable,
                    allow_scripts: event.allow_scripts,
                    artifact_id: event.artifact_id,
                    metadata: event.metadata,
                  });
                } else if (event.type === 'soulmate_response') {
                  const speechText = soulmateSpeechText(event);
                  if (speechText === null) {
                    lifecycle.fail(INVALID_SOULMATE_RESPONSE_ERROR);
                  } else {
                    lifecycle.markResponse();
                    callbacks.onContent?.(speechText);
                  }
                } else if (event.type === 'message_delta') {
                  if (event.usage && callbacks.onMetrics) {
                    callbacks.onMetrics({
                      input_tokens: event.usage.input_tokens || 0,
                      output_tokens: event.usage.output_tokens || 0,
                    });
                  }
                } else if (event.type === 'message_stop') {
                  lifecycle.complete(event);
                } else if (event.type === 'error' || event.error) {
                  lifecycle.fail(event.error?.message || 'Unknown error');
                }

                // Handle topic_id from response
                if (event.topic_id && callbacks.onInit) {
                  callbacks.onInit({ topic_id: event.topic_id });
                }
              } catch (e) {
                // Ignore parse errors for partial data
              }
              if (lifecycle.isTerminal()) {
                streamDone = true;
                break;
              }
            }
          }
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          lifecycle.fail(error.message);
        }
      });

    return controller;
  },

  /**
   * Send streaming chat message with full event handling
   * Supports tool calls, metrics, and progress events
   */
  sendStreaming: (
    agentType: string,
    request: ChatRequest,
    callbacks: StreamCallbacks
  ): AbortController => {
    const controller = new AbortController();
    const lifecycle = createStreamLifecycle(callbacks);

    const token = localStorage.getItem('admin_token');
    const userId = localStorage.getItem('admin_user_id');

    fetch(`${API_BASE_URL}/v1/chat/completions?stream_format=anthropic`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-User-ID': userId || '',
        'X-Stream-Format': 'anthropic',
        'X-Client-Source': 'admin-console',
      },
      body: JSON.stringify({
        model: agentType,
        messages: request.messages,
        stream: true,
        topic_id: request.topic_id,
        images: request.images, // Include images for multimodal input
      }),
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No reader available');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        // Track tool_use blocks for input streaming
        const toolUseBlocks = new Map<number, { id: string; name: string; inputJson: string; isReasoning: boolean }>();
        // Track active reasoning IDs (from reasoning_started events)
        const activeReasoningIds = new Set<string>();
        // Pattern to detect malformed ReasoningStep str() output
        const REASONING_PATTERN = /title='[^']*'\s+action='[^']*'\s+result=/;
        let streamDone2 = false;

        while (!streamDone2) {
          const { done, value } = await reader.read();

          if (done) {
            streamDone2 = true;
            lifecycle.fail(INCOMPLETE_STREAM_ERROR);
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              // Event type line - we handle data in the data: line
              continue;
            }

            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();

              if (data === '[DONE]') {
                lifecycle.complete({});
                streamDone2 = true;
                break;
              }

              try {
                const event = JSON.parse(data);
                const blockIndex = typeof event.index === 'number' ? event.index : -1;

                // Handle Anthropic format events (all events have 'type' field)
                if (event.type === 'message_start') {
                  callbacks.onInit?.(event);
                } else if (event.type === 'reasoning_started') {
                  // Agno custom event: reasoning started
                  if (event.reasoning_id) {
                    activeReasoningIds.add(event.reasoning_id);
                  }
                } else if (event.type === 'reasoning_completed') {
                  // Agno custom event: reasoning completed
                  if (event.reasoning_id) {
                    activeReasoningIds.delete(event.reasoning_id);
                  }
                } else if (event.type === 'content_block_start') {
                  if (event.content_block?.type === 'tool_use') {
                    lifecycle.markResponse();
                    const toolId = event.content_block.id || `tool_${Date.now()}`;
                    const toolName = event.content_block.name || 'unknown';
                    // Check if this tool is marked as reasoning
                    // Backend must set is_reasoning or x_agno_is_reasoning flags
                    const isReasoning = activeReasoningIds.has(toolId) ||
                      event.content_block.is_reasoning === true ||
                      event.content_block.x_agno_is_reasoning === true;
                    // Track tool_use block
                    if (blockIndex >= 0) {
                      toolUseBlocks.set(blockIndex, {
                        id: toolId,
                        name: toolName,
                        inputJson: event.content_block.input ? JSON.stringify(event.content_block.input) : '',
                        isReasoning
                      });
                    }
                    callbacks.onToolCallStart?.({
                      name: toolName,
                      id: toolId,
                      input: event.content_block.input,
                    });
                  }
                } else if (event.type === 'content_block_delta') {
                  // Handle input_json_delta for streaming tool input
                  if (event.delta?.type === 'input_json_delta' && event.delta.partial_json) {
                    if (blockIndex >= 0 && toolUseBlocks.has(blockIndex)) {
                      const toolInfo = toolUseBlocks.get(blockIndex)!;
                      toolInfo.inputJson += event.delta.partial_json;
                      // Try to parse and update for reasoning tools
                      if (toolInfo.isReasoning) {
                        try {
                          const parsedInput = JSON.parse(toolInfo.inputJson);
                          callbacks.onToolInputUpdate?.(toolInfo.id, parsedInput);
                        } catch {
                          // JSON not complete yet
                        }
                      }
                    }
                  } else if (event.delta?.type === 'text_delta' && event.delta.text) {
                    const text = event.delta.text;
                    // Check if we have active reasoning tools
                    const hasActiveReasoningTool = Array.from(toolUseBlocks.values()).some(t => t.isReasoning)
                      || activeReasoningIds.size > 0;
                    // Filter malformed ReasoningStep content
                    if (hasActiveReasoningTool && REASONING_PATTERN.test(text)) {
                      // Skip this content - it's reasoning tool result leaking to main content
                      continue;
                    }
                    lifecycle.markResponse();
                    callbacks.onContent?.(text);
                  }
                } else if (event.type === 'content_block_stop') {
                  // Clean up tool tracking and send final input
                  if (blockIndex >= 0 && toolUseBlocks.has(blockIndex)) {
                    const toolInfo = toolUseBlocks.get(blockIndex)!;
                    if (toolInfo.isReasoning && toolInfo.inputJson) {
                      try {
                        const parsedInput = JSON.parse(toolInfo.inputJson);
                        callbacks.onToolInputUpdate?.(toolInfo.id, parsedInput);
                      } catch {
                        // JSON parse failed
                      }
                    }
                    toolUseBlocks.delete(blockIndex);
                  }
                  // Legacy format for tool metadata
                  if (event.x_agno_tool_name && callbacks.onToolCallEnd) {
                    callbacks.onToolCallEnd({
                      toolName: event.x_agno_tool_name,
                      executionTime: event.x_agno_execution_time,
                      images: event.x_agno_tool_images || [],
                    });
                  }
                } else if (event.type === 'tool_metadata') {
                  // Tool execution metadata
                  callbacks.onToolCallEnd?.({
                    toolName: event.toolName,
                    executionTime: event.executionTime,
                    images: event.images || [],
                  });
                } else if (event.type === 'tool_progress') {
                  if (event.tool_id && event.message) {
                    callbacks.onToolProgress?.(event.tool_id, event.message);
                  }
                } else if (event.type === 'chart_svg' || event.event_type === 'chart_svg') {
                  lifecycle.markResponse();
                  // Chart visualization event from astrology tools
                  callbacks.onChartSvg?.({
                    event_type: 'chart_svg',
                    chart_type: event.chart_type || 'natal',
                    svg_content: event.svg_content || '',
                    subject_name: event.subject_name || '',
                    chart_title: event.chart_title,
                    theme: event.theme || 'classic',
                    output_type: event.output_type || 'full',
                    width: event.width || 870,
                    height: event.height || 550,
                    metadata: event.metadata,
                  });
                } else if (event.type === 'html_content' || event.event_type === 'html_content') {
                  lifecycle.markResponse();
                  // HTML content event (K-line charts, interactive reports)
                  callbacks.onHtmlContent?.({
                    event_type: 'html_content',
                    content_type: event.content_type || 'custom',
                    html_content: event.html_content || '',
                    title: event.title || '',
                    description: event.description,
                    width: event.width || 800,
                    height: event.height || 500,
                    expandable: event.expandable,
                    allow_scripts: event.allow_scripts,
                    artifact_id: event.artifact_id,
                    metadata: event.metadata,
                  });
                } else if (event.type === 'soulmate_response') {
                  const speechText = soulmateSpeechText(event);
                  if (speechText === null) {
                    lifecycle.fail(INVALID_SOULMATE_RESPONSE_ERROR);
                  } else {
                    lifecycle.markResponse();
                    callbacks.onContent?.(speechText);
                  }
                } else if (event.type === 'message_delta') {
                  if (event.usage && callbacks.onMetrics) {
                    callbacks.onMetrics({
                      input_tokens: event.usage.input_tokens || 0,
                      output_tokens: event.usage.output_tokens || 0,
                    });
                  }
                } else if (event.type === 'message_stop') {
                  lifecycle.complete(event);
                } else if (event.type === 'error' || event.error) {
                  lifecycle.fail(event.error?.message || 'Unknown error');
                }

                // Handle topic_id from response
                if (event.topic_id && callbacks.onInit) {
                  callbacks.onInit({ topic_id: event.topic_id });
                }
              } catch (e) {
                // Ignore parse errors for partial data
              }
              if (lifecycle.isTerminal()) {
                streamDone2 = true;
                break;
              }
            }
          }
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          lifecycle.fail(error.message);
        }
      });

    return controller;
  },

  /**
   * Get chat topics for an agent
   * Backend returns List[TopicListResponse] directly
   */
  getTopics: async (agentType: string, limit = 50, offset = 0): Promise<ChatTopic[]> => {
    const { data } = await userApiClient.get(`/agents/${agentType}/topics`, {
      params: { limit, offset },
    });
    // Backend returns array directly, map last_active to updated_at for compatibility
    const topics = Array.isArray(data) ? data : (data.topics || []);
    return topics.map((t: any) => ({
      ...t,
      updated_at: t.updated_at || t.last_active,
    }));
  },

  /**
   * Get topic detail with all messages
   * Returns the complete chat history for a specific topic
   */
  getTopicDetail: async (agentType: string, topicId: string): Promise<any> => {
    const { data } = await userApiClient.get(`/agents/${agentType}/topics/${topicId}`);
    return data;
  },

  /**
   * Get messages for a topic (extracted from topic detail)
   */
  getTopicMessages: async (agentType: string, topicId: string): Promise<TopicMessage[]> => {
    const { data } = await userApiClient.get(`/agents/${agentType}/topics/${topicId}`);
    // Extract messages from runs in topic detail
    const messages: TopicMessage[] = [];
    if (data.runs) {
      for (const run of data.runs) {
        // Add user message
        if (run.message) {
          messages.push({
            id: `user_${messages.length}`,
            role: run.message.role || 'user',
            content: run.message.content || '',
            created_at: run.response?.created_at || data.created_at,
          });
        }
        // Add assistant response
        if (run.response) {
          messages.push({
            id: run.response.run_id || `assistant_${messages.length}`,
            role: 'assistant',
            content: run.response.content || '',
            created_at: run.response.created_at || data.created_at,
            tool_calls: run.response.tool_calls,
            metrics: run.response.metrics,
          });
        }
      }
    }
    return messages;
  },

  /**
   * Delete a topic
   */
  deleteTopic: async (agentType: string, topicId: string): Promise<void> => {
    await userApiClient.delete(`/agents/${agentType}/topics/${topicId}`);
  },

  /**
   * Rename a topic
   */
  renameTopic: async (agentType: string, topicId: string, name: string): Promise<void> => {
    await userApiClient.post(`/agents/${agentType}/topics/${topicId}/rename`, { name });
  },

  /**
   * Create new topic
   */
  createTopic: async (agentType: string): Promise<{ topic_id: string }> => {
    const { data } = await userApiClient.post(`/agents/${agentType}/topics`);
    return data;
  },
};

export default chatApi;
